// The action bar's own items and saved loadouts (issue 1130; docs/VTT/design/29-the-attack-chain.md §2.1). A bar item is
// one of the token's attacks (`/actions`, issue 1127, by its `key`) or a SAVED SPELL its owner or the DM wrote: an area
// template's shape and size (`areas.ts`) and the save fields `area.resolve` takes (issue 1129). A loadout is a named set
// of item keys; the active one is what the bar shows. Both live on the token (`TableObject.spells`, `.loadouts`), are
// written WHOLE by `object.spells` / `object.loadouts` (the token's owners and the DM), and are projected to the owners
// and the DM alone: another player who can see the token is sent neither key (hidden means absent).
import { AREA_LIMITS, AREA_SHAPES } from './areas.js';
import { parseDamageText } from '../rules/attack.js';
import { ON_SAVE, SAVE_LIMITS } from '../rules/save.js';
const isIds = (v) => Array.isArray(v) && v.length <= 40 && v.every((x) => typeof x === 'string' && x.length <= 64);
export function parseHolding(v) {
    if (!isObj(v) || !isText(v.name, 1, SPELL_LIMITS.name) || !isIds(v.effects) || !isIds(v.areas))
        return null;
    if (v.spell !== undefined && !isItemKey(v.spell))
        return null;
    if (!(typeof v.since === 'number' && Number.isInteger(v.since) && v.since >= 0))
        return null;
    if (v.roll !== undefined && !isText(v.roll, 1, 64))
        return null;
    return { name: v.name.trim(), ...(typeof v.spell === 'string' ? { spell: v.spell } : {}), effects: [...v.effects], areas: [...v.areas], since: v.since, ...(typeof v.roll === 'string' ? { roll: v.roll } : {}) };
}
export const copyHolding = (h) => ({ ...h, effects: [...h.effects], areas: [...h.areas] });
/** A slot by the end of its sheet field's key: `level_2`, or `pact`. */
export const SLOT_RE = /^(level_[1-9][0-9]?|pact)$/;
/** The slots with one left that a spell of this level can be cast with, the least first. Pure. */
export function slotChoices(slots, level) {
    return slots.filter((s) => s.left > 0 && (s.level === null || s.level >= level)).sort((a, b) => (a.level ?? level) - (b.level ?? level));
}
/** A slot in the player's words. */
export const slotWords = (which) => (which === 'pact' ? 'pact slot' : `level ${which.replace(/^level_/, '')} slot`);
/**
 * A spell's damage at this cast. Its text, or the highest `atLevels` the character level reaches; then each slot level
 * above its own adds `upcast`, joined to dice of the same size. Null: it has none. Pure.
 */
export function spellDamageAt(s, at = {}) {
    const reached = at.level == null ? [] : Object.keys(s.atLevels ?? {}).map(Number).filter((n) => n <= at.level).sort((a, b) => b - a);
    const text = reached.length ? s.atLevels[String(reached[0])] : s.damage;
    const base = text ? parseDamageText(text) : null;
    const above = at.slot == null || s.level === undefined ? 0 : at.slot - s.level;
    const more = above > 0 && s.upcast ? parseDamageText(s.upcast) : null;
    if (!base || !more)
        return base;
    const parts = base.parts.map((p) => ({ ...p }));
    for (const m of more.parts) {
        const same = parts.find((p) => p.sides === m.sides);
        if (same)
            same.count += m.count * above;
        else
            parts.push({ ...m, count: m.count * above, extra: true });
    }
    return { ...base, parts, bonus: base.bonus + more.bonus * above };
}
/** The attack a spell with `attack` makes, through the attack flow: the sheet's spell bonus as its one term. Pure. */
export function spellAttackSpec(s, bonus, defence, damage) {
    return { name: s.name, terms: bonus === null ? [] : [{ label: 'Spell attack', value: bonus }], defence, ranged: s.attack !== 'melee', ...(damage ? { damage } : {}) };
}
/** A sheet row's id as the table keeps it on a spell (qa-runs 1180): the site's row ids, at most 64 characters. */
export const SPELL_ROW_RE = /^[A-Za-z0-9_-]{1,64}$/;
/** A casting time longer than a round (qa-runs 408): cast in a fight, it is outside a turn's actions. */
export const LONG_CAST = 'long';
const normAction = (s) => s.toLowerCase().replace(/[-_]/g, ' ').replace(/\s+/g, ' ').trim().replace(/\s*actions?$/, '').trim();
const COUNT_WORDS = { a: 1, an: 1, one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, ten: 10 };
/**
 * A spell entry's `casting_time` text read against the system's action kinds (qa-runs 408): the kind's key, `LONG_CAST`,
 * or null when it does not read. A kind is found by its own label or key ("1 swift action" is the kind labelled "Swift
 * action"); the d20 books' other phrasings are read as they print: "1 action" a standard action, "1 round" and
 * "1 full round" a full-round action; minutes, hours, days or more than one round are longer than a round. Only the
 * first of several phrasings ("1 standard action or see text") is read.
 */
export function castingTimeOf(text, actions) {
    const first = text.toLowerCase().split(/[;,(]|\bor\b|\bsee\b/)[0].trim();
    const m = /^(\d+|a|an|one|two|three|four|five|six|ten)?\s*(.*)$/.exec(first);
    if (!m || !m[2])
        return null;
    const n = m[1] === undefined ? 1 : /^\d+$/.test(m[1]) ? Number(m[1]) : COUNT_WORDS[m[1]];
    let rest = m[2].trim();
    if (/^(minute|min|hour|hr|day|week|month|year)s?\b/.test(rest))
        return LONG_CAST;
    if (/^(full[- ]?)?rounds?$/.test(rest)) {
        if (n > 1)
            return LONG_CAST;
        rest = 'full round';
    }
    if (!actions || n !== 1)
        return null;
    let want = normAction(rest);
    // "1 action": the d20 books' standard action, by the system's kind with that key.
    if (want === '' && /^actions?$/.test(rest))
        want = 'standard';
    if (!want)
        return null;
    const kind = actions.kinds.find((k) => normAction(k.label) === want || normAction(k.key) === want);
    return kind ? kind.key : null;
}
/** The generic pictures a box may wear (qa-runs#369 slice B): the client draws each as its own inline SVG. */
export const HOT_ICONS = [
    'sword', 'bow', 'shield', 'fire', 'cold', 'lightning', 'skull', 'heart', 'eye', 'dice', 'potion', 'muscle',
    'key', 'foot', 'scroll', 'moon', 'sparkle', 'fog', 'axe', 'staff', 'star', 'hand',
];
export const isHotIcon = (v) => typeof v === 'string' && HOT_ICONS.includes(v);
const PICTURE_ID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
/** Every uploaded picture a token's presets' boxes wear (qa-runs#369 slice B): what the DM may fetch through them. */
export function hotPictures(l) {
    const out = new Set();
    for (const x of l?.list ?? [])
        for (const b of x.boxes ?? [])
            if (b?.picture)
                out.add(b.picture);
    return out;
}
export const HOT_LIMITS = { boxes: 40, ref: 90, label: 40, hotkey: 24 };
const HOT_REF_RE = /^(attack|spell|sheet-spell|special|save|skill|ability|maneuver|toggle):[^\u0000-\u001f\u007f]{1,80}$|^dice:[1-9][0-9]?d[1-9][0-9]{0,2}([+-][0-9]{1,3})?$/;
const HOTKEY_RE = /^(Ctrl\+)?(Alt\+)?(Shift\+)?([A-Za-z0-9]|F[1-9][0-2]?|Tab|[-=,./;'`\[\]])$/;
export const isHotRef = (v) => typeof v === 'string' && v.length <= HOT_LIMITS.ref && HOT_REF_RE.test(v);
export const isHotkey = (v) => typeof v === 'string' && v.length <= HOT_LIMITS.hotkey && HOTKEY_RE.test(v);
function parseHotBox(v) {
    if (v === null)
        return null;
    if (!isObj(v) || !isHotRef(v.ref))
        return undefined;
    if (v.label !== undefined && !isText(v.label, 1, HOT_LIMITS.label))
        return undefined;
    if (v.hotkey !== undefined && !isHotkey(v.hotkey))
        return undefined;
    if (v.icon !== undefined && !isHotIcon(v.icon))
        return undefined;
    if (v.picture !== undefined && !(typeof v.picture === 'string' && PICTURE_ID_RE.test(v.picture)))
        return undefined;
    return {
        ref: v.ref, ...(typeof v.label === 'string' ? { label: v.label.trim() } : {}), ...(typeof v.hotkey === 'string' ? { hotkey: v.hotkey } : {}),
        ...(typeof v.icon === 'string' ? { icon: v.icon } : {}), ...(typeof v.picture === 'string' ? { picture: v.picture } : {}),
    };
}
export const SPELL_LIMITS = { spells: 40, name: AREA_LIMITS.label, saveLine: 80, damage: 80, loadouts: 12, keys: 40, loadoutName: 40 };
const CONTROL_RE = /[\u0000-\u001f\u007f]/;
const KEY_RE = /^[a-z0-9][a-z0-9_-]{0,63}$/;
/** An action kind's key, as `encounter.action` takes it, or `LONG_CAST`. */
const CAST_KEY_RE = /^[a-z][a-z0-9_]{0,62}$/;
const isObj = (v) => typeof v === 'object' && v !== null && !Array.isArray(v);
const isText = (v, min, max) => typeof v === 'string' && v.trim().length >= min && v.length <= max && !CONTROL_RE.test(v);
const isSquares = (v) => typeof v === 'number' && Number.isInteger(v) && v >= 1 && v <= AREA_LIMITS.squares;
export const isItemKey = (v) => typeof v === 'string' && KEY_RE.test(v);
/** One spell from the wire or the store: exact shape or null. Unknown fields are dropped. */
export function parseSpell(v) {
    if (!isObj(v) || !isItemKey(v.key) || !isText(v.name, 1, SPELL_LIMITS.name))
        return null;
    if (!AREA_SHAPES.includes(v.shape) || !isSquares(v.size))
        return null;
    const rect = v.shape === 'rect';
    if (v.size2 !== undefined && !isSquares(v.size2))
        return null;
    if (!isText(v.saveLine, 0, SPELL_LIMITS.saveLine))
        return null;
    const saveLine = v.saveLine.trim();
    const dc = v.dc === undefined ? null : v.dc;
    if (dc !== null && !(typeof dc === 'number' && Number.isInteger(dc) && dc >= 1 && dc <= SAVE_LIMITS.dc))
        return null;
    if (saveLine && dc === null)
        return null;
    if (v.damage !== undefined && (!isText(v.damage, 1, SPELL_LIMITS.damage) || !parseDamageText(v.damage)))
        return null;
    if (v.onSave !== undefined && !ON_SAVE.includes(v.onSave))
        return null;
    if (v.castingTime !== undefined && !(typeof v.castingTime === 'string' && CAST_KEY_RE.test(v.castingTime)))
        return null;
    if (v.row !== undefined && !(typeof v.row === 'string' && SPELL_ROW_RE.test(v.row)))
        return null;
    if (v.noArea !== undefined && v.noArea !== true)
        return null;
    if (v.pending !== undefined && !(typeof v.pending === 'number' && Number.isInteger(v.pending) && v.pending >= 0 && v.pending <= 99))
        return null;
    if (v.pendingSlots !== undefined && !(Array.isArray(v.pendingSlots) && v.pendingSlots.length <= 99 && v.pendingSlots.every((w) => typeof w === 'string' && SLOT_RE.test(w))))
        return null;
    if (v.level !== undefined && !(typeof v.level === 'number' && Number.isInteger(v.level) && v.level >= 0 && v.level <= 99))
        return null;
    if (v.attack !== undefined && v.attack !== 'ranged' && v.attack !== 'melee')
        return null;
    if ((v.ritual !== undefined && v.ritual !== true) || (v.concentration !== undefined && v.concentration !== true))
        return null;
    if (v.upcast !== undefined && (!isText(v.upcast, 1, SPELL_LIMITS.damage) || !parseDamageText(v.upcast)))
        return null;
    if (v.atLevels !== undefined && !(isObj(v.atLevels) && Object.keys(v.atLevels).length <= 20 && Object.entries(v.atLevels).every(([k, d]) => /^[1-9][0-9]?$/.test(k) && isText(d, 1, SPELL_LIMITS.damage) && parseDamageText(d))))
        return null;
    return {
        key: v.key, name: v.name.trim(), shape: v.shape, size: v.size,
        ...(rect ? { size2: v.size2 ?? v.size } : {}),
        saveLine, dc: saveLine ? dc : null,
        ...(typeof v.damage === 'string' ? { damage: v.damage.trim() } : {}),
        ...(saveLine && v.onSave !== undefined ? { onSave: v.onSave } : {}),
        ...(typeof v.castingTime === 'string' ? { castingTime: v.castingTime } : {}),
        ...(typeof v.row === 'string' ? { row: v.row } : {}),
        ...(v.noArea === true ? { noArea: true } : {}),
        ...(typeof v.row === 'string' && typeof v.pending === 'number' && v.pending > 0 ? { pending: v.pending, ...(Array.isArray(v.pendingSlots) && v.pendingSlots.length ? { pendingSlots: [...v.pendingSlots] } : {}) } : {}),
        ...(typeof v.level === 'number' ? { level: v.level } : {}),
        ...(v.attack === 'ranged' || v.attack === 'melee' ? { attack: v.attack } : {}),
        ...(v.ritual === true ? { ritual: true } : {}),
        ...(v.concentration === true ? { concentration: true } : {}),
        ...(typeof v.upcast === 'string' ? { upcast: v.upcast.trim() } : {}),
        ...(isObj(v.atLevels) ? { atLevels: { ...v.atLevels } } : {}),
    };
}
/** A token's whole list of spells: at most `SPELL_LIMITS.spells`, keys distinct. Null when any one is malformed. */
export function parseSpells(v) {
    if (!Array.isArray(v) || v.length > SPELL_LIMITS.spells)
        return null;
    const out = [];
    for (const x of v) {
        const s = parseSpell(x);
        if (!s)
            return null;
        out.push(s);
    }
    return new Set(out.map((s) => s.key)).size === out.length ? out : null;
}
/** A token's loadouts: at most 12, 40 keys each, names 1–40 characters and distinct; `active` one of them or null. */
export function parseLoadouts(v) {
    if (!isObj(v) || !Array.isArray(v.list) || v.list.length > SPELL_LIMITS.loadouts)
        return null;
    const list = [];
    for (const l of v.list) {
        if (!isObj(l) || !isText(l.name, 1, SPELL_LIMITS.loadoutName))
            return null;
        if (!Array.isArray(l.keys) || l.keys.length > SPELL_LIMITS.keys || !l.keys.every(isItemKey))
            return null;
        const keys = [...new Set(l.keys)];
        let boxes;
        if (l.boxes !== undefined) {
            if (!Array.isArray(l.boxes) || l.boxes.length > HOT_LIMITS.boxes)
                return null;
            boxes = [];
            for (const b of l.boxes) {
                const box = parseHotBox(b);
                if (box === undefined)
                    return null;
                boxes.push(box);
            }
        }
        if (l.hotkey !== undefined && !isHotkey(l.hotkey))
            return null;
        list.push({ name: l.name.trim(), keys, ...(boxes ? { boxes } : {}), ...(typeof l.hotkey === 'string' ? { hotkey: l.hotkey } : {}) });
    }
    if (new Set(list.map((l) => l.name)).size !== list.length)
        return null;
    const active = v.active === undefined ? null : v.active;
    if (active !== null && (typeof active !== 'string' || !list.some((l) => l.name === active)))
        return null;
    return { list, active: active };
}
export const copySpells = (s) => s.map((x) => ({ ...x, ...(x.pendingSlots ? { pendingSlots: [...x.pendingSlots] } : {}), ...(x.atLevels ? { atLevels: { ...x.atLevels } } : {}) }));
export const copyLoadouts = (l) => ({
    list: l.list.map((x) => ({ name: x.name, keys: [...x.keys], ...(x.boxes ? { boxes: x.boxes.map((b) => (b ? { ...b } : null)) } : {}), ...(x.hotkey ? { hotkey: x.hotkey } : {}) })),
    active: l.active,
});
//# sourceMappingURL=spells.js.map